import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { use, useEffect, useRef, useState } from 'react';
import LocaleSwitcher from '@/website/components/Locale/LocaleSwitcher';
import ChooseUserPortalDialog from '@/website/components/dialogs/chooseUserPortal/index';
import { useUserAuthLoginCheck } from '@/website/hooks/auth/useUserAuthLoginCheck';
import { useQuoteReleatedQuote } from '@/website/hooks/useQuoteReleatedQuote';
import { useUserPortal } from '@/website/hooks/useUserPortal';
import { useOrganizationLandingPageStore } from '@/website/store/OrganizationLandingPageStore';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { JoveButton } from '@jove-frontend/mui';
import {
  AmericanCountryList,
  Button,
  JoveInternationalizationCountrySwitchView,
  JoveInternationalizationLanguageSwitchView,
  PromotionEntityType,
  PromotionGetResponse,
  UKCountryList,
  businessInsuranceKey,
  jumpToUserPortal,
  removeAllJoveAppAuthKeys,
  useLocalTranslation,
  useOnClickOutside,
} from '@jove-frontend/uikit';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { useAnalytics } from 'use-analytics';
import { useAuthStore } from '../../store/AuthStore';
import { Routes, routeHasCompanySlug } from '../../util/routes';
import LanguageSwitcher, { LanguageSwitcherDynamicParamsInterface } from '../Locale/LanguageSwitcher';
import ValidationButton from '../ValidationButton';
import { GetQuoteButton } from './GetQuoteButton';

type Props = {
  productsBarOpen: boolean;
  joveCommunityBarOpen?: boolean;
  isMobile?: boolean;
  toggleProductsBar: (args: boolean) => void;
  openQuoteModal: () => void;
  usecasesBarOpen?: boolean;
  toggleUsecasesBar?: (args: boolean) => void;
  toggleJoveCommunityBar?: (args: boolean) => void;
  closeSubMenus?: () => void;
  promotion?: PromotionGetResponse;
  countrySwitchCanSelectCountryList?: any[];
  selectCountry?: string;
  selectCountryChange?: (country: string) => void;
  languageSwitchCanSelectLanguageList?: any[];
  selectLanguage?: string;
  selectLanguageChange?: (language: string) => void;
} & LanguageSwitcherDynamicParamsInterface;

export const NavBar = ({
  productsBarOpen,
  joveCommunityBarOpen,
  isMobile,
  toggleProductsBar,
  openQuoteModal,
  usecasesBarOpen,
  toggleUsecasesBar,
  toggleJoveCommunityBar,
  closeSubMenus,
  promotion,
  insuranceType,
  productScheme,
  countrySwitchCanSelectCountryList,
  selectCountry,
  selectCountryChange,
  languageSwitchCanSelectLanguageList,
  selectLanguage,
  selectLanguageChange,
}: Props) => {
  const { track } = useAnalytics();
  const { $t } = useLocalTranslation();
  const navRef = useRef();
  const router = useRouter();
  const { isAuthenticated, logout, loginWithRedirect } = useAuth0();
  const { user, clientPortalUrl, isCommunityUser } = useAuthStore(({ state }) => state);
  const [isFromLandingLink] = useState(!!router.query['promotionSlug']);
  const [showChoosePortalDialog, setShowChoosePortalDialog] = useState(false);
  const { userPortalList } = useUserPortal();
  const { jumpToQuotePageAction } = useQuoteReleatedQuote();
  const {
    state: { organization },
  } = useOrganizationLandingPageStore();
  const {
    state: { promotionInfo },
  } = promotionInfoStore();
  const [displayInfoMap, setDisplayInfoMap] = useState({
    ourProducts: false,
  });
  const { goToAuthLoginCheckPage } = useUserAuthLoginCheck();
  const [loggingOut, setLoggingOut] = useState(false);

  const isCompany = router.asPath.startsWith('/business') || routeHasCompanySlug(router.query);

  useOnClickOutside(navRef, () => {
    if (closeSubMenus) closeSubMenus();
  });

  /**
   * effects
   */
  useEffect(() => {
    const isCompany = router.asPath.startsWith('/business') || routeHasCompanySlug(router.query);
    const { payload, promotion_type, insurance_type } = promotionInfo || {};

    setDisplayInfoMap({
      ourProducts:
        (!isCompany && !isFromLandingLink) ||
        ([PromotionEntityType.BROKER_PROMOTION, PromotionEntityType.PARTNER_PROMOTION].includes(promotion_type) &&
          insurance_type === businessInsuranceKey &&
          payload?.landing_page_theme?.length > 1),
    });
  }, [router, promotionInfo, isFromLandingLink]);

  /**
   * methods
   */
  const openQuotePageAction = () => {
    jumpToQuotePageAction();
  };

  const goToPortalAction = () => {
    if (userPortalList.length === 1) {
      const item = userPortalList[0];
      jumpToUserPortal(router, item);
    } else {
      setShowChoosePortalDialog(true);
    }
  };

  const closeChoosePortalDialogAction = () => {
    setShowChoosePortalDialog(false);
  };

  const loginInAction = () => {
    // goToAuthLoginCheckPage();
    loginWithRedirect({
      appState: {
        returnTo: window.location.origin,
      },
    });
  };

  const goToBrokerPageAction = () => {
    router.replace(Routes.brokerLandingPage());
  };

  const logoutAction = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    await removeAllJoveAppAuthKeys();
    // website signs out in place: clear the auth state without navigating, then reload the current page
    await logout({ openUrl: false });
    window.location.reload();
  };

  if (!isMobile) {
    return (
      <div className="navbar flex-1 items-center justify-end space-x-4" ref={navRef}>
        {displayInfoMap?.ourProducts && (
          <button className="hidden text-sm font-semibold hover:text-gray-900 md:inline-block" onClick={() => toggleProductsBar(productsBarOpen)}>
            {$t('text:navigation.our_products')}{' '}
            {productsBarOpen ? <ChevronUpIcon className="inline-block h-4" /> : <ChevronDownIcon className="inline-block h-4" />}
          </button>
        )}
        {!isCompany && !isFromLandingLink && !router.asPath.startsWith('/personal') && !router.asPath.startsWith('/wamo') && (
          <>
            <button className="hidden text-sm font-semibold hover:text-gray-900 md:inline-block" onClick={() => toggleUsecasesBar(usecasesBarOpen)}>
              {$t('text:navigation.usecases')}{' '}
              {usecasesBarOpen ? <ChevronUpIcon className="inline-block h-4" /> : <ChevronDownIcon className="inline-block h-4" />}
            </button>
          </>
        )}

        {(!user || isCommunityUser) && !isFromLandingLink && (
          <button
            className="hidden text-sm font-semibold hover:text-gray-900 md:inline-block"
            onClick={() => toggleJoveCommunityBar(joveCommunityBarOpen)}
          >
            {$t('text:navigation.jove_community')}{' '}
            {joveCommunityBarOpen ? <ChevronUpIcon className="inline-block h-4" /> : <ChevronDownIcon className="inline-block h-4" />}
          </button>
        )}

        {!promotion && !organization && (
          <>
            <NavButton label={$t('page:footerLinks.contact_us')} href={Routes.contactUs()} />
          </>
        )}
        {!router.query['promotionSlug'] && (
          <>
            <JoveButton size="large" onClick={goToBrokerPageAction}>
              {$t('text:navigation.brokers')}
            </JoveButton>
          </>
        )}
        {!isCompany && <GetQuoteButton openQuoteModal={openQuotePageAction} />}
        {!promotion && (
          <>
            <ValidationButton />
          </>
        )}
        {/* <LocaleSwitcher promotion={promotionInfo} />
        <LanguageSwitcher insuranceType={insuranceType} productScheme={productScheme} /> */}
        <JoveInternationalizationCountrySwitchView
          countryList={countrySwitchCanSelectCountryList}
          value={selectCountry}
          valueChange={selectCountryChange}
          promotionInfo={promotionInfo}
        />
        {![...AmericanCountryList, ...UKCountryList].includes(selectCountry) && (
          <JoveInternationalizationLanguageSwitchView
            languageList={languageSwitchCanSelectLanguageList}
            value={selectLanguage}
            valueChange={selectLanguageChange}
          />
        )}

        {isAuthenticated && !promotion && (
          <>
            {userPortalList.length > 0 && (
              <Button color="secondary-outline" onClick={goToPortalAction}>
                <span>{$t('text:navigation.switch_portal')}</span>
              </Button>
            )}
            <Button color="secondary-outline" loading={loggingOut} disabled={loggingOut} onClick={logoutAction}>
              <span>{$t('text:navigation.log_out')}</span>
            </Button>
          </>
        )}

        {!isAuthenticated && !promotion && (
          <Button color="secondary-outline" onClick={loginInAction}>
            <span>{$t('text:navigation.sign_in')}</span>
          </Button>
        )}

        {showChoosePortalDialog && <ChooseUserPortalDialog close={closeChoosePortalDialogAction} />}
      </div>
    );
  }

  return (
    <div className="navbar flex flex-col items-start justify-start space-y-3">
      {(!user || isCommunityUser) && (
        <>
          <NavButton label={$t('text:navigation.brokers')} href={Routes.brokerLandingPage()} isMobile={isMobile} />
          <NavButton label={$t('text:navigation.blog')} href={Routes.blog(router.query['company-slug'] as string)} isMobile={isMobile} />
          <NavButton label={$t('text:navigation.guides')} href={Routes.guides(router.query['company-slug'] as string)} isMobile={isMobile} />
          {/* JOV-5434: jobs-api decommissioned, hide Jobs nav link */}
          {/* <NavButton label={$t('text:navigation.jobs')} href={Routes.jobs(router.query['company-slug'] as string)} isMobile={isMobile} /> */}
          <NavButton label={$t('text:navigation.ir35')} href={Routes.ir35(router.query['company-slug'] as string)} isMobile={isMobile} />
          <NavButton label={$t('page:footerLinks.contact_us')} href={Routes.contactUs()} isMobile={isMobile} />
        </>
      )}

      {/* {!isAuthenticated && (
        <Button
          color="secondary-outline"
          onClick={() =>
            loginWithRedirect({
              appState: {
                returnTo:
                  window.location.origin + Routes.authenticated(router.asPath),
              },
            })
          }
        >
          <span>Sign in</span>
        </Button>
      )}

      {isAuthenticated &&
        (clientPortalUrl ? (
          <Button
            color="secondary-outline"
            onClick={() => window.location.replace(clientPortalUrl)}
          >
            <span>Go to Portal</span>
          </Button>
        ) : (
          <Button
            color="secondary-outline"
            onClick={() =>
              logout({ logoutParams: { returnTo: window.location.origin } })
            }
          >
            <span>Log out</span>
          </Button>
        ))} */}

      {isAuthenticated ? (
        <>
          {userPortalList.length > 0 && (
            <Button color="secondary-outline" onClick={goToPortalAction}>
              <span>{$t('text:navigation.switch_portal')}</span>
            </Button>
          )}
          <Button color="secondary-outline" loading={loggingOut} disabled={loggingOut} onClick={logoutAction}>
            <span>{$t('text:navigation.log_out')}</span>
          </Button>
        </>
      ) : (
        <Button color="secondary-outline" onClick={loginInAction}>
          <span>{$t('text:navigation.sign_in')}</span>
        </Button>
      )}
      {showChoosePortalDialog && <ChooseUserPortalDialog dialogContainerWidth="100%" close={closeChoosePortalDialogAction} />}
    </div>
  );
};

type NavButtonProps = LinkProps & {
  label: string;
  isMobile?: boolean;
};

export const NavButton = ({ label, href, isMobile }: NavButtonProps) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const isActive = router.asPath === href.toString() || router.asPath.startsWith(href.toString());

  const isCompanyPage = routeHasCompanySlug(router.query);

  const className = clsx('nav-link text-sm font-semibold numeric-font-fix', {
    active: isActive,
    'hover:text-gray-900': !isMobile,
    'is-mobile text-white hover:text-white-900': isMobile && !routeHasCompanySlug(router.query),
    'is-mobile text-black hover:text-black/80': isMobile && routeHasCompanySlug(router.query),
  });

  return (
    <Box className="flex flex-row items-center space-x-2">
      <Link
        aria-label={label}
        href={href}
        className={className}
        onClick={() => {
          track('click_nav_link', {
            label,
            href,
          });
        }}
      >
        <span>{label}</span>
        <span className={`border ${isCompanyPage ? 'bg-black' : 'bg-white'}`}></span>
      </Link>
      {isMobile && <ArrowRightIcon className={clsx('inline-block h-4 text-white')} />}
    </Box>
  );
};
